/**
 * Posts service layer.
 * Abstracts all Firestore operations for posts from the rest of the application.
 * Uses subcollections for likes and comments, with batch writes for atomic updates.
 * Supports multiple post types: text, workout, meal, progress.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  where,
  serverTimestamp,
  writeBatch,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { uploadPostImage, deletePostImage } from './storage.service';
import { handleServiceError } from '../utils/error-handler.util';
import { extractHashtags, extractMentions } from '../utils/text-parser.util';

const POSTS_COLLECTION = 'posts';

/**
 * Serialize a Firestore document snapshot into a plain object.
 * Converts Firestore Timestamps to JS Date ISO strings.
 * Handles all post types (text, workout, meal, progress).
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object} A plain post object.
 */
const serializePost = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    type: data.type || 'text',
    text: data.text || '',
    imageUrl: data.imageUrl || null,
    authorId: data.authorId || '',
    authorName: data.authorName || '',
    authorUsername: data.authorUsername || '',
    authorAvatarUrl: data.authorAvatarUrl || null,
    likesCount: data.likesCount || 0,
    commentsCount: data.commentsCount || 0,
    edited: data.edited || false,
    tags: data.tags || [],
    mentions: data.mentions || [],
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt instanceof Timestamp
      ? data.updatedAt.toDate().toISOString()
      : data.updatedAt || null,
    // Type-specific fields
    workout: data.workout || null,
    meal: data.meal || null,
    progress: data.progress || null,
    _docSnapshot: docSnap, // Used internally for pagination cursor
  };
};

/**
 * Create a new post in Firestore.
 * Uploads image(s) if provided, then creates the post document.
 * Supports all post types: text, workout, meal, progress.
 * @param {Object} params
 * @param {string} [params.type='text'] - Post type: 'text' | 'workout' | 'meal' | 'progress'.
 * @param {string} params.text - The text content / caption of the post.
 * @param {File} [params.imageFile] - Optional image file to upload.
 * @param {Object} params.author - The author's profile data.
 * @param {string} params.author.uid - The author's user ID.
 * @param {string} params.author.displayName - The author's display name.
 * @param {string} params.author.username - The author's username.
 * @param {string} [params.author.avatarUrl] - The author's avatar URL.
 * @param {function} [params.onImageProgress] - Optional image upload progress callback.
 * @param {Object} [params.workout] - Workout-specific data (when type === 'workout').
 * @param {Object} [params.meal] - Meal-specific data (when type === 'meal').
 * @param {Object} [params.progress] - Progress-specific data (when type === 'progress').
 * @param {File} [params.beforeImageFile] - Before image for progress posts.
 * @param {File} [params.afterImageFile] - After image for progress posts.
 * @returns {Promise<Object>} The created post object with its Firestore ID.
 */
export const createPost = async ({
  type = 'text',
  text,
  imageFile,
  author,
  onImageProgress,
  workout,
  meal,
  progress,
  beforeImageFile,
  afterImageFile,
}) => {
  try {
    // Create a placeholder doc first to get the ID for image storage path
    const postRef = doc(collection(db, POSTS_COLLECTION));
    const postId = postRef.id;

    let imageUrl = null;
    if (imageFile) {
      imageUrl = await uploadPostImage(imageFile, postId, onImageProgress);
    }

    // Handle progress photo uploads (before/after)
    let progressData = progress ? { ...progress } : null;
    if (type === 'progress' && progressData) {
      if (beforeImageFile) {
        progressData.beforeImageUrl = await uploadPostImage(
          beforeImageFile,
          `${postId}_before`,
          onImageProgress
        );
      }
      if (afterImageFile) {
        progressData.afterImageUrl = await uploadPostImage(
          afterImageFile,
          `${postId}_after`,
          onImageProgress
        );
      }
    }

    const trimmedText = (text || '').trim();
    const tags = extractHashtags(trimmedText);
    const mentionUsernames = extractMentions(trimmedText);

    const postData = {
      type,
      text: trimmedText,
      imageUrl,
      authorId: author.uid,
      authorName: author.displayName,
      authorUsername: author.username,
      authorAvatarUrl: author.avatarUrl || null,
      likesCount: 0,
      commentsCount: 0,
      edited: false,
      tags,
      mentions: mentionUsernames,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Add type-specific fields
    if (type === 'workout' && workout) {
      postData.workout = workout;
    }
    if (type === 'meal' && meal) {
      postData.meal = meal;
    }
    if (type === 'progress' && progressData) {
      postData.progress = progressData;
    }

    await setDoc(postRef, postData);

    // Update tag counts asynchronously (fire-and-forget)
    if (tags.length > 0) {
      import('./search.service').then((searchSvc) => {
        searchSvc.incrementTagCounts(tags);
      }).catch(() => {});
    }

    return {
      id: postId,
      ...postData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _docSnapshot: null,
    };
  } catch (error) {
    handleServiceError(error, 'Create post', 'Failed to create post. Please try again.');
  }
};

/**
 * Retrieve paginated posts from Firestore, ordered by createdAt descending.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - The last document snapshot for pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of posts to retrieve per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getPosts = async (lastDoc = null, pageLimit = 10) => {
  try {
    const postsRef = collection(db, POSTS_COLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(serializePost);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { posts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get posts', 'Failed to load posts. Please try again.');
  }
};

/**
 * Retrieve paginated posts filtered by type from Firestore.
 * @param {string} type - Post type to filter by: 'text' | 'workout' | 'meal' | 'progress'.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of posts per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getPostsByType = async (type, lastDoc = null, pageLimit = 10) => {
  try {
    const postsRef = collection(db, POSTS_COLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        postsRef,
        where('type', '==', type),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        postsRef,
        where('type', '==', type),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(serializePost);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { posts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get posts by type', 'Failed to load posts. Please try again.');
  }
};

/**
 * Retrieve a single post by its Firestore document ID.
 * @param {string} postId - The Firestore document ID of the post.
 * @returns {Promise<Object|null>} The post object or null if not found.
 */
export const getPostById = async (postId) => {
  try {
    const docRef = doc(db, POSTS_COLLECTION, postId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return serializePost(docSnap);
  } catch (error) {
    handleServiceError(error, 'Get post by ID', 'Failed to load post. Please try again.');
  }
};

/**
 * Update an existing post in Firestore.
 * Sets edited=true and updates the updatedAt timestamp.
 * @param {string} postId - The Firestore document ID of the post to update.
 * @param {Object} params
 * @param {string} [params.text] - Updated text content.
 * @param {File} [params.imageFile] - New image file to upload (replaces existing).
 * @param {string} [params.existingImageUrl] - The current image URL (for deletion if replacing).
 * @param {function} [params.onImageProgress] - Optional image upload progress callback.
 * @returns {Promise<Object>} The updated post object.
 */
export const updatePost = async (postId, { text, imageFile, existingImageUrl, onImageProgress }) => {
  try {
    const updateData = {
      edited: true,
      updatedAt: serverTimestamp(),
    };

    if (text !== undefined) {
      const trimmedText = text.trim();
      updateData.text = trimmedText;
      updateData.tags = extractHashtags(trimmedText);
      updateData.mentions = extractMentions(trimmedText);
    }

    if (imageFile) {
      // Delete old image if exists
      if (existingImageUrl) {
        await deletePostImage(existingImageUrl);
      }
      updateData.imageUrl = await uploadPostImage(imageFile, postId, onImageProgress);
    }

    const docRef = doc(db, POSTS_COLLECTION, postId);
    await updateDoc(docRef, updateData);

    const updatedSnap = await getDoc(docRef);
    return serializePost(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Update post', 'Failed to update post. Please try again.');
  }
};

/**
 * Delete a post and its associated image from Firestore and Storage.
 * @param {string} postId - The Firestore document ID of the post to delete.
 * @param {string} [imageUrl] - The image URL to delete from Storage.
 * @returns {Promise<string>} The ID of the deleted post.
 */
export const deletePost = async (postId, imageUrl) => {
  try {
    // Delete the image from Storage if it exists
    if (imageUrl) {
      await deletePostImage(imageUrl);
    }

    // Delete the post document
    const docRef = doc(db, POSTS_COLLECTION, postId);
    await deleteDoc(docRef);

    return postId;
  } catch (error) {
    handleServiceError(error, 'Delete post', 'Failed to delete post. Please try again.');
  }
};

/**
 * Add a like to a post using a batch write for atomicity.
 * Creates a like subdocument and increments the likesCount.
 * @param {string} postId - The Firestore document ID of the post.
 * @param {string} userId - The ID of the user liking the post.
 * @returns {Promise<void>}
 */
export const likePost = async (postId, userId) => {
  try {
    const batch = writeBatch(db);

    // Add like subdocument
    const likeRef = doc(db, POSTS_COLLECTION, postId, 'likes', userId);
    batch.set(likeRef, { createdAt: serverTimestamp() });

    // Increment likesCount on the post
    const postRef = doc(db, POSTS_COLLECTION, postId);
    batch.update(postRef, { likesCount: increment(1) });

    await batch.commit();
  } catch (error) {
    handleServiceError(error, 'Like post', 'Failed to like post. Please try again.');
  }
};

/**
 * Remove a like from a post using a batch write for atomicity.
 * Deletes the like subdocument and decrements the likesCount.
 * @param {string} postId - The Firestore document ID of the post.
 * @param {string} userId - The ID of the user unliking the post.
 * @returns {Promise<void>}
 */
export const unlikePost = async (postId, userId) => {
  try {
    const batch = writeBatch(db);

    // Remove like subdocument
    const likeRef = doc(db, POSTS_COLLECTION, postId, 'likes', userId);
    batch.delete(likeRef);

    // Decrement likesCount on the post
    const postRef = doc(db, POSTS_COLLECTION, postId);
    batch.update(postRef, { likesCount: increment(-1) });

    await batch.commit();
  } catch (error) {
    handleServiceError(error, 'Unlike post', 'Failed to unlike post. Please try again.');
  }
};

/**
 * Check if a user has liked a specific post.
 * @param {string} postId - The Firestore document ID of the post.
 * @param {string} userId - The user ID to check.
 * @returns {Promise<boolean>} True if the user has liked the post.
 */
export const checkIfLiked = async (postId, userId) => {
  try {
    const likeRef = doc(db, POSTS_COLLECTION, postId, 'likes', userId);
    const likeSnap = await getDoc(likeRef);
    return likeSnap.exists();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Check if liked]', error);
    }
    return false;
  }
};

/**
 * Retrieve paginated posts by a specific user, ordered by createdAt descending.
 * @param {string} userId - The user ID to filter by.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of posts per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getPostsByUser = async (userId, lastDoc = null, pageLimit = 10) => {
  try {
    const postsRef = collection(db, POSTS_COLLECTION);
    let q;

    if (lastDoc) {
      q = query(
        postsRef,
        where('authorId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        firestoreLimit(pageLimit)
      );
    } else {
      q = query(
        postsRef,
        where('authorId', '==', userId),
        orderBy('createdAt', 'desc'),
        firestoreLimit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(serializePost);
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === pageLimit;

    return { posts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get posts by user', 'Failed to load user posts. Please try again.');
  }
};

/**
 * Retrieve personalized feed posts from followed users + own posts.
 * Firestore `in` queries support up to 30 values, so for larger following lists
 * we split into multiple queries and merge results.
 * @param {string[]} authorIds - Array of user IDs (following + self).
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of posts per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getPostsFeed = async (authorIds, lastDoc = null, pageLimit = 10) => {
  try {
    if (!authorIds || authorIds.length === 0) {
      return { posts: [], lastDoc: null, hasMore: false };
    }

    const postsRef = collection(db, POSTS_COLLECTION);

    // Firestore 'in' queries support up to 30 values
    const CHUNK_SIZE = 30;
    const chunks = [];
    for (let i = 0; i < authorIds.length; i += CHUNK_SIZE) {
      chunks.push(authorIds.slice(i, i + CHUNK_SIZE));
    }

    let allPosts = [];

    for (const chunk of chunks) {
      let q;
      if (lastDoc) {
        q = query(
          postsRef,
          where('authorId', 'in', chunk),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          firestoreLimit(pageLimit)
        );
      } else {
        q = query(
          postsRef,
          where('authorId', 'in', chunk),
          orderBy('createdAt', 'desc'),
          firestoreLimit(pageLimit)
        );
      }

      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map(serializePost);
      allPosts = [...allPosts, ...posts];
    }

    // Sort merged results by createdAt descending and limit
    allPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const limitedPosts = allPosts.slice(0, pageLimit);

    const lastVisible = limitedPosts.length > 0
      ? limitedPosts[limitedPosts.length - 1]._docSnapshot
      : null;
    const hasMore = allPosts.length >= pageLimit;

    return { posts: limitedPosts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get posts feed', 'Failed to load feed. Please try again.');
  }
};

/**
 * Retrieve personalized feed posts filtered by type from followed users + own posts.
 * Combines the authorId 'in' query with a type filter.
 * @param {string[]} authorIds - Array of user IDs (following + self).
 * @param {string} type - Post type to filter by: 'text' | 'workout' | 'meal' | 'progress'.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDoc - Pagination cursor.
 * @param {number} [pageLimit=10] - Maximum number of posts per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getPostsFeedByType = async (authorIds, type, lastDoc = null, pageLimit = 10) => {
  try {
    if (!authorIds || authorIds.length === 0) {
      return { posts: [], lastDoc: null, hasMore: false };
    }

    const postsRef = collection(db, POSTS_COLLECTION);

    // Firestore 'in' queries support up to 30 values
    const CHUNK_SIZE = 30;
    const chunks = [];
    for (let i = 0; i < authorIds.length; i += CHUNK_SIZE) {
      chunks.push(authorIds.slice(i, i + CHUNK_SIZE));
    }

    let allPosts = [];

    for (const chunk of chunks) {
      let q;
      if (lastDoc) {
        q = query(
          postsRef,
          where('authorId', 'in', chunk),
          where('type', '==', type),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          firestoreLimit(pageLimit)
        );
      } else {
        q = query(
          postsRef,
          where('authorId', 'in', chunk),
          where('type', '==', type),
          orderBy('createdAt', 'desc'),
          firestoreLimit(pageLimit)
        );
      }

      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map(serializePost);
      allPosts = [...allPosts, ...posts];
    }

    // Sort merged results by createdAt descending and limit
    allPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const limitedPosts = allPosts.slice(0, pageLimit);

    const lastVisible = limitedPosts.length > 0
      ? limitedPosts[limitedPosts.length - 1]._docSnapshot
      : null;
    const hasMore = allPosts.length >= pageLimit;

    return { posts: limitedPosts, lastDoc: lastVisible, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get posts feed by type', 'Failed to load feed. Please try again.');
  }
};

/**
 * Get post IDs that a user has liked. Used for the profile "Likes" tab.
 * Scans posts and checks the likes subcollection for the given userId.
 * @param {string} userId - The user ID whose liked posts to find.
 * @param {import('firebase/firestore').DocumentSnapshot|null} lastDocCursor - Pagination cursor for the main posts query.
 * @param {number} [pageLimit=10] - Maximum number of liked posts per page.
 * @returns {Promise<{posts: Array<Object>, lastDoc: import('firebase/firestore').DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getLikedPosts = async (userId, lastDocCursor = null, pageLimit = 10) => {
  try {
    // We need to scan posts to find ones with a like from this user
    // This approach queries recent posts and filters by like existence
    const postsRef = collection(db, POSTS_COLLECTION);
    const fetchLimit = pageLimit * 3; // Fetch more to account for filtering

    let q;
    if (lastDocCursor) {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDocCursor),
        firestoreLimit(fetchLimit)
      );
    } else {
      q = query(
        postsRef,
        orderBy('createdAt', 'desc'),
        firestoreLimit(fetchLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const likedPosts = [];
    let lastVisibleDoc = null;

    for (const postDoc of querySnapshot.docs) {
      if (likedPosts.length >= pageLimit) break;
      const likeRef = doc(db, POSTS_COLLECTION, postDoc.id, 'likes', userId);
      const likeSnap = await getDoc(likeRef);
      if (likeSnap.exists()) {
        likedPosts.push(serializePost(postDoc));
      }
      lastVisibleDoc = postDoc;
    }

    const hasMore = querySnapshot.docs.length === fetchLimit;

    return { posts: likedPosts, lastDoc: lastVisibleDoc, hasMore };
  } catch (error) {
    handleServiceError(error, 'Get liked posts', 'Failed to load liked posts. Please try again.');
  }
};
